"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { assigneeMessage, cancelAssignment, completeAssignment, isOverdue, reopenAssignment } from "@/lib/breach/assignments"
import type { Assignment, Workspace } from "@/lib/breach/workspace"
import { AssignForm } from "./assign-form"
import type { GoTo } from "./incident-nav"
import type { WorkspaceUpdater } from "./incident-tools"

const when = (iso?: string) => (iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "")
const toLocalInput = (d: Date) => { const x = new Date(d); x.setMinutes(x.getMinutes() - x.getTimezoneOffset()); return x.toISOString().slice(0, 16) }

function Row({ a, w, update, go }: { a: Assignment; w: Workspace; update: WorkspaceUpdater; go: GoTo }) {
  const [doneAt, setDoneAt] = useState(toLocalInput(new Date()))
  const [copied, setCopied] = useState(false)
  const contact = a.contactId ? w.contacts.find((c) => c.id === a.contactId) : undefined
  const overdue = isOverdue(a)

  return (
    <li className={cn("grid gap-2 rounded-lg border p-3 text-sm", overdue ? "border-destructive/60" : "border-border")}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className={cn("text-base font-medium", a.status !== "open" && "text-muted-foreground", a.status === "done" && "line-through")}>{a.title}</p>
        <span className="text-xs text-muted-foreground">
          {a.status === "done" ? `Done ${when(a.doneAt)}` : a.status === "cancelled" ? "Cancelled" : a.dueAt ? `Needed by ${when(a.dueAt)}` : "Open"}
          {overdue && <strong className="ml-2 text-destructive">Overdue</strong>}
        </span>
      </div>
      <p className="text-muted-foreground">
        {a.assigneeName || "Unassigned"}
        {contact?.role && contact.name ? ` · ${contact.role}` : ""}
        {contact?.phone && <> · <a className="underline" href={`tel:${contact.phone}`}>{contact.phone}</a></>}
        {a.itemId && <> · <button type="button" className="underline" onClick={() => go({ v: "index" })}>from your plan</button></>}
      </p>
      {a.note && <p className="text-xs text-muted-foreground">{a.note}</p>}

      <div className="flex flex-wrap items-center gap-2 print:hidden">
        {a.status === "open" && (
          <>
            <details className="rounded border border-border px-2 py-1">
              <summary className="cursor-pointer text-sm font-medium">Mark done</summary>
              <label className="mt-2 grid gap-1 text-xs">When was it done?
                <input type="datetime-local" className="rounded-md border border-border bg-background p-2 text-sm" value={doneAt} onChange={(e) => setDoneAt(e.target.value)} />
              </label>
              <Button size="sm" className="mt-2" onClick={() => update((x) => completeAssignment(x, a.id, doneAt ? new Date(doneAt) : new Date()))}>Save</Button>
            </details>
            <Button size="sm" variant="outline" onClick={async () => { try { await navigator.clipboard.writeText(assigneeMessage(a)); setCopied(true); setTimeout(() => setCopied(false), 1500) } catch { /* ignore */ } }}>
              {copied ? "Copied" : "Copy a message to send them"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => update((x) => cancelAssignment(x, a.id))}>Cancel it</Button>
          </>
        )}
        {a.status !== "open" && <Button size="sm" variant="outline" onClick={() => update((x) => reopenAssignment(x, a.id))}>Reopen</Button>}
      </div>
    </li>
  )
}

/** Everything handed to someone, linked to Contacts. Finishing a task puts the time on the Timeline. */
export function AssignmentsView({ w, update, go }: { w: Workspace; update: WorkspaceUpdater; go: GoTo }) {
  const [adding, setAdding] = useState(false)
  const open = w.assignments.filter((a) => a.status === "open")
  const done = w.assignments.filter((a) => a.status === "done")
  const cancelled = w.assignments.filter((a) => a.status === "cancelled")

  return (
    <section aria-labelledby="as-h" className="max-w-3xl">
      <h1 id="as-h" className="text-xl font-semibold sm:text-2xl">Assignments</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Tasks you&apos;ve handed to someone in your <button type="button" className="underline" onClick={() => go({ v: "contacts" })}>Contacts</button>. When one is done, mark it
        here and the time goes on your Timeline. You can also assign any item from your plan.
      </p>
      <div className="mt-4 print:hidden">
        {adding ? <AssignForm w={w} update={update} onClose={() => setAdding(false)} /> : <Button onClick={() => setAdding(true)}>Add an assignment</Button>}
      </div>

      {w.assignments.length === 0 && !adding && <p className="mt-6 rounded-lg border border-border p-4 text-sm text-muted-foreground">Nothing assigned yet. Handing a task to someone and recording when it&apos;s done gives you a clear record of who did what.</p>}

      {open.length > 0 && (<><h2 className="mt-6 text-lg font-semibold">Open ({open.length})</h2><ul className="mt-2 grid gap-2">{open.map((a) => <Row key={a.id} a={a} w={w} update={update} go={go} />)}</ul></>)}
      {done.length > 0 && (<><h2 className="mt-6 text-lg font-semibold">Done ({done.length})</h2><ul className="mt-2 grid gap-2">{done.map((a) => <Row key={a.id} a={a} w={w} update={update} go={go} />)}</ul></>)}
      {cancelled.length > 0 && (<details className="mt-6"><summary className="cursor-pointer text-sm text-muted-foreground">Cancelled ({cancelled.length})</summary><ul className="mt-2 grid gap-2">{cancelled.map((a) => <Row key={a.id} a={a} w={w} update={update} go={go} />)}</ul></details>)}
    </section>
  )
}
